import { StorageFields } from 'applicaiton/constants/localStorage';
import {
  AuthLocalStorage,
  getIsShowMyAgenda,
  getIsUserPrivateDoctor,
  getUserId,
  getUserProfileFromLocalStorage,
  getUserRole,
} from 'applicaiton/sessionStorage/auth';
import { store } from 'applicaiton/store';
import { clearStore } from 'applicaiton/store/reducers/Users/UsersSlice';
import { PathNames } from 'applicaiton/routes';
import { userHasPermission } from 'common/helpers/userHasPermission';
import { UserPermissions } from 'applicaiton/constants/userPermissions';
import { UserRoles } from 'applicaiton/constants/userRoles';
import { getCurrentRoutes } from 'applicaiton/routes/routes';

const logoutChannel = new BroadcastChannel('logout');
const loginChannel = new BroadcastChannel('login');

/**
 * Logs out the user and redirects to the login page
 */
export const logout = () => {
  logoutChannel.postMessage('Logout');
  window.location.href = window.location.origin + PathNames.login;
  store?.dispatch(clearStore());
};

/**
 * Logs in the user and redirects them to the appropriate page based on their role and permissions
 */
export const login = () => {
  const doShowAgenda = userHasPermission(UserPermissions.accessToOtherAgendas);
  const userRole = getUserRole();
  const userId = getUserId();
  const isProfessionalRole = userRole === UserRoles.professional;
  const isShowMyAgenda = getIsShowMyAgenda();
  const isPrivateDoctor = getIsUserPrivateDoctor();
  const userProfile = getUserProfileFromLocalStorage();
  const isClinicOwner = userRole?.includes(UserRoles.clinicOwner);

  const homePath = getCurrentRoutes()[0]?.path;

  const showAgendaOfOthers =
    (doShowAgenda && !isProfessionalRole) || isProfessionalRole;

  loginChannel.postMessage('Login');

  if (isPrivateDoctor) {
    if (!userProfile?.isFirstLoginCompleted) {
      window.location.href =
        window.location.origin + `${PathNames.profile}/${userId}`;
    } else {
      isShowMyAgenda
        ? (window.location.href = window.location.origin + homePath)
        : (window.location.href =
            window.location.origin + `${PathNames.profile}/${userId}`);
    }
  } else {
    if (isClinicOwner && !userProfile?.isFirstLoginCompleted) {
      window.location.href = window.location.origin + PathNames.createClinic;
    } else {
      showAgendaOfOthers &&
        (window.location.href = window.location.origin + homePath);
    }
  }

  sessionStorage.removeItem('schedulerFilters');
};

/**
 * Logs out all tabs when the user logs out
 */
export const logoutAllTabs = () => {
  logoutChannel.onmessage = () => {
    logout();
    logoutChannel.close();
  };
};

/**
 * Logs in all tabs when the user logs in
 */
export const loginAllTabs = () => {
  loginChannel.onmessage = () => {
    login();
    loginChannel.close();
  };
};

/**
 * Provides authentication-related functionality for saving and retrieving tokens, checking auth status, and cleaning storage
 */
export const authService = {
  /**
   * Saves an access token to local storage
   * @param jwt - The JWT to save
   */
  saveAccessToken(jwt: string) {
    localStorage.setItem(StorageFields.fbToken, jwt);
  },

  /**
   * Retrieves the saved access token from local storage
   * @returns The saved access token, or null if not found
   */
  getAccessToken() {
    return localStorage.getItem(StorageFields.fbToken);
  },

  /**
   * Checks whether an access token is currently saved in local storage
   * @returns True if an access token is saved, false otherwise
   */
  checkAuth() {
    return !!localStorage.getItem(AuthLocalStorage.accessToken);
  },

  /**
   * Removes the saved access token from local storage
   */
  removeToken() {
    localStorage.removeItem(AuthLocalStorage.accessToken);
  },

  /**
   * Saves a user's email address to local storage
   * @param email - The email address to save
   */
  saveEmail(email: string) {
    localStorage.setItem(StorageFields.email, email);
  },

  /**
   * Retrieves the saved email address from local storage
   * @returns The saved email address, or null if not found
   */
  getEmail() {
    return localStorage.getItem(StorageFields.email);
  },

  /**
   * Saves a verification code to local storage
   * @param code - The verification code to save
   */
  saveCode(code: number) {
    localStorage.setItem(StorageFields.verificationCode, String(code));
  },

  /**
   * Retrieves the saved verification code from local storage
   * @returns The saved verification code, or null if not found
   */
  getEmailVerifiationCode() {
    return localStorage.getItem(StorageFields.verificationCode);
  },

  /**
   * Saves a timer value to local storage
   * @param timer - The timer value to save
   */
  saveTimer(timer: string) {
    localStorage.setItem(StorageFields.timer, timer);
  },

  /**
   * Retrieves the saved timer value from local storage
   * @returns The saved timer value, or null if not found
   */
  getTimer() {
    return localStorage.getItem(StorageFields.timer);
  },

  /**
   * Removes the saved timer value from local storage
   */
  removeTimer() {
    localStorage.removeItem(StorageFields.timer);
  },

  /**
   * Clears all data in both local and session storage
   */
  cleanStorage() {
    localStorage.clear();
    sessionStorage.clear();
  },

  /**
   * Removes the saved access token from local storage
   */
  eraseSessionData() {
    this.removeToken();
  },
};
